import { mdiDelete, mdiEye, mdiPlus } from "@mdi/js";
import { Button, ModalFuncProps, Space, Tag } from "antd";
import confirm from "antd/lib/modal/confirm";
import { ExamTypeVm, FailureStatusVm } from "api/generated/lumen";
import { useDispatch, useSelector } from "app/store";
import { MaterialIcon } from "components/MaterialIcon";
import BooleanDisplay from "components/ui/BooleanDisplay";
import IconButton from "components/ui/IconButton";
import useTable from "hooks/useTable";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "widgets/table/CustomTable";
import { ColumnStorageName } from "widgets/table/table-settings/ColumnStorageName";
import {
  ColumnState,
  ExtendedColumnType,
} from "widgets/table/table-settings/ExtendedColumnType";
import {
  ColumnConfigParams,
  ListRequestParams,
} from "widgets/table/useTableUtils";
import StatusCreateModal from "./StatusCreateModal";
import { enumTranslate } from "utils/enumTranslate";
import Claims from "features/auth/claims";
import { Permission } from "components/Permission";

const StatusesList: FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { listParams, list } = useSelector((state) => state.statuses);
  const { fetchList } = useSelector((state) => state.loading.effects.statuses);

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const fetchTableData = useCallback(
    (requestParams: ListRequestParams) =>
      dispatch.statuses.fetchList(requestParams),
    [dispatch.statuses]
  );

  const columnParams = useMemo<ColumnConfigParams<FailureStatusVm>[]>(
    () => [
      {
        key: "name",
        defaultState: ColumnState.AlwaysVisible,
        title: t("settings.statuses.name"),
      },
      {
        key: "alternativeName",
        defaultState: ColumnState.AlwaysVisible,
        title: t("settings.statuses.alternativeName"),
      },
      {
        key: "isSpecialCase",
        defaultState: ColumnState.AlwaysVisible,
        title: t("settings.statuses.isSpecialCase"),
        render: (value: boolean) => {
          return (
            <BooleanDisplay
              value={value}
              trueDisplay={<Tag color="success">{t("common.yes")}</Tag>}
              falseDisplay={<Tag color="default">{t("common.no")}</Tag>}
            />
          );
        },
      },
      {
        key: "isSealed",
        defaultState: ColumnState.AlwaysVisible,
        title: t("settings.statuses.isSealed"),
        render: (value: boolean) => {
          return (
            <BooleanDisplay
              value={value}
              trueDisplay={<Tag color="success">{t("common.yes")}</Tag>}
              falseDisplay={<Tag color="default">{t("common.no")}</Tag>}
            />
          );
        },
      },
      {
        key: "isDefault",
        defaultState: ColumnState.AlwaysVisible,
        title: t("settings.statuses.isDefault"),
        render: (value: boolean) => {
          return (
            <BooleanDisplay
              value={value}
              trueDisplay={<Tag color="success">{t("common.yes")}</Tag>}
              falseDisplay={<Tag color="default">{t("common.no")}</Tag>}
            />
          );
        },
      },
      {
        key: "requiresWorkbookTask",
        defaultState: ColumnState.AlwaysVisible,
        title: t("settings.statuses.requiresWorkbookTask"),
        render: (value: boolean) => {
          return (
            <BooleanDisplay
              value={value}
              trueDisplay={<Tag color="success">{t("common.yes")}</Tag>}
              falseDisplay={<Tag color="default">{t("common.no")}</Tag>}
            />
          );
        },
      },
      {
        key: "requiresStateChangeReason",
        defaultState: ColumnState.AlwaysVisible,
        title: t("settings.statuses.requiresStateChangeReason"),
        render: (value) => enumTranslate(value, "requiresStateChangeReason"),
        width: "13rem",
      },
    ],
    [t]
  );

  const handleViewButtonClick = useCallback(
    (id?: string) => {
      dispatch.statuses.setEditingItemId(id);

      setCreateModalOpen(true);
    },
    [dispatch.statuses]
  );

  const handleNewButtonClick = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const handleDeleteButtonClick = useCallback(
    (id?: string) => {
      if (id) {
        const confirmProps: ModalFuncProps = {
          okText: t("common.confirmDeleteModal.yes"),
          okType: "danger",
          cancelText: t("common.confirmDeleteModal.no"),
          title: t("common.confirmDeleteModal.title"),
          onOk: async () => {
            await dispatch.statuses.deleteStatus(id);
            await dispatch.statuses.fetchList(listParams);
          },
        };

        confirm(confirmProps);
      }
    },
    [dispatch.statuses, listParams, t]
  );

  const actionColumnParams = useMemo<
    Partial<ExtendedColumnType<ExamTypeVm>> | undefined
  >(
    () => ({
      fixed: "right",
      columnOrderFixed: true,
      width: 90,
      render(item: any, record) {
        return (
          <Space>
            <IconButton
              path={mdiEye}
              onClick={() => handleViewButtonClick(record.id)}
              title={t("common.details")}
            />

            <Permission acceptableClaims={[Claims.FailureStatus.Write]}>
              <IconButton
                path={mdiDelete}
                onClick={() => handleDeleteButtonClick(record.id)}
                title={t("common.delete")}
              />
            </Permission>
          </Space>
        );
      },
      title: t("common.table.actions"),
    }),
    [handleDeleteButtonClick, handleViewButtonClick, t]
  );

  const customTableProps = useTable({
    fetchTableData,
    columnParams,
    actionColumnParams,
    listParams,
    list,
    columnStorageName: ColumnStorageName.STATUSES,
    headerOptions: (
      <Permission acceptableClaims={[Claims.FailureStatus.Write]}>
        <Button
          icon={<MaterialIcon path={mdiPlus} />}
          type="primary"
          onClick={handleNewButtonClick}
        >
          {t("common.new")}
        </Button>
      </Permission>
    ),
    resetListParams: dispatch.statuses.resetListParams,
    loading: fetchList.loading,
    resetStore: dispatch.statuses.reset,
  });

  return (
    <>
      <CustomTable<FailureStatusVm>
        {...customTableProps}
        scrollX={1600}
        selectable={false}
      />

      <StatusCreateModal
        visible={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        fetchDataParams={listParams}
      />
    </>
  );
};

export default StatusesList;
